import { create } from "zustand";
import type {
  GroupSummary,
  AddAssignedRatingsRequest,
  AddAssignedRatingsResult,
  AssignedRatingPatch,
  ChangeReason,
  GroupDetail,
  GroupPatch,
  GroupTotals,
  GroupWithMembers,
  MemberDraft,
  MemberFlagsPatch,
  MemberAssignedRating,
  GroupStatus,
  MemberWithRatings,
  ReopenGroupResult,
  RestoreVersionMode,
  RestoreVersionResult,
  SendToSalesResult,
  ToggleMemberExcludedResult,
  TopCondition,
} from "../types";
import { getApi } from "../api";
import { adjustedRmm, topConditions } from "../calc";
import { reportError } from "../services/eventBus";
import { trackEvent } from "../telemetry/appInsights";

export type LoadState = { kind: "idle" } | { kind: "loading" } | { kind: "error"; message: string } | { kind: "ready" };


interface GroupState {
  loadState: LoadState;
  group: GroupWithMembers | null;
  totals: GroupTotals | null;
  topConditions: readonly TopCondition[];
  /** Other versions of the loaded group on the same Opportunity, oldest first (versions list and Send to Sales summary). */
  otherVersions: readonly GroupSummary[];
  /** True while a save that can move the totals is in flight; the tiles show the numbers as recalculating until the engine answers. */
  isRecalculating: boolean;
  loadGroup: (groupId: string, signal?: AbortSignal) => Promise<void>;
  applyDetail: (detail: GroupDetail) => void;
  clearGroup: () => void;
  updateGroup: (patch: GroupPatch) => Promise<boolean>;
  setOverrideMultiplierPreview: (multiplier: number) => void;
  toggleMemberExcluded: (memberId: string, excluded: boolean) => Promise<ToggleMemberExcludedResult | null>;
  /** IRIX Checked and Signed Auth through the flag endpoint: no engine run, only the row and the group status change in state. */
  updateMemberFlags: (memberId: string, patch: MemberFlagsPatch) => Promise<boolean>;
  /** IRIX Source through the flag endpoint; anything with a change reason, notes, or a debit goes through updateAssignedRating. */
  setAssignedRatingIrixSource: (memberId: string, assignedRatingId: string, irixSource: boolean) => Promise<boolean>;
  saveMember: (memberId: string | null, draft: MemberDraft) => Promise<MemberWithRatings | null>;
  removeMember: (memberId: string) => Promise<boolean>;
  addAssignedRatings: (memberId: string, request: AddAssignedRatingsRequest) => Promise<AddAssignedRatingsResult | null>;
  updateAssignedRating: (memberId: string, assignedRatingId: string, patch: AssignedRatingPatch) => Promise<boolean>;
  removeAssignedRating: (memberId: string, assignedRatingId: string, changeReason?: ChangeReason | null, changeExplanation?: string | null) => Promise<boolean>;
  sendToSales: () => Promise<SendToSalesResult | null>;
  reopenGroup: () => Promise<ReopenGroupResult | null>;
  restoreVersion: (mode: RestoreVersionMode) => Promise<RestoreVersionResult | null>;
}

function detailSlice(detail: GroupDetail) {
  return { group: detail.group, totals: detail.totals, topConditions: topConditions(detail.group, 3), loadState: { kind: "ready" } as LoadState };
}

/**
 * Flag saves (IRIX Source on a rating; IRIX Checked and Signed Auth on a member) cannot change a debit, so the API saves
 * them without the engine and answers with the group status only. These apply the saved fields, and that status, to the
 * copy in state instead of replacing the whole group.
 */
function withRatingFields(group: GroupWithMembers, memberId: string, assignedRatingId: string, fields: Partial<Pick<MemberAssignedRating, "irixSource" | "isExcluded">>, status: GroupStatus): GroupWithMembers {
  return {
    ...group,
    status,
    members: group.members.map((member) =>
      member.id !== memberId ? member : { ...member, assignedRatings: member.assignedRatings.map((rating) => (rating.id !== assignedRatingId ? rating : { ...rating, ...definedFields(fields) })) },
    ),
  };
}

function withMemberFields(group: GroupWithMembers, memberId: string, fields: MemberFlagsPatch & Partial<Pick<MemberWithRatings, "isExcluded">>, status: GroupStatus): GroupWithMembers {
  return { ...group, status, members: group.members.map((member) => (member.id !== memberId ? member : { ...member, ...definedFields(fields) })) };
}

/** Whether a save's answer still carries the rating; the engine drops Doubled Build and Doubled Hypertension rows when their trigger goes. */
function hasRating(detail: GroupDetail, memberId: string, assignedRatingId: string): boolean {
  return detail.group.members.some((member) => member.id === memberId && member.assignedRatings.some((rating) => rating.id === assignedRatingId));
}

/** A PATCH omits what it does not change; only the keys it carries may overwrite the row. */
function definedFields<T extends object>(patch: T): Partial<T> {
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined)) as Partial<T>;
}

async function loadOtherVersions(group: GroupWithMembers): Promise<GroupSummary[]> {
  try {
    const page = await getApi().searchGroups({ opportunityId: group.opportunityId, includeSuperseded: true, pageSize: 100 });
    return page.items.filter((entry) => entry.id !== group.id).sort((a, b) => a.version - b.version);
  } catch {
    return [];
  }
}

export const useGroupStore = create<GroupState>()((set, get) => {
  const requireGroupId = (): string => {
    const group = get().group;
    if (!group) throw new Error("No group is loaded.");
    return group.id;
  };

  /**
   * Saves run one at a time, in click order, and only the last response is applied. Two fast exclude clicks would otherwise
   * race: the first response carries the group as it was after the first change alone and would undo the second click's
   * flip until its own response landed, and if the responses crossed the screen would end on the stale one. Queuing keeps
   * every response cumulative; applying only when the queue drains keeps the optimistic flips on screen meanwhile, with the
   * totals marked as recalculating the whole time. Failures are reported per save; a failed save still lets the last good
   * response through so the totals match what actually saved.
   */
  let queue: Promise<unknown> = Promise.resolve();
  let pendingSaves = 0;
  let lastGoodDetail: GroupDetail | null = null;

  const mutate = <T>(context: string, action: (groupId: string, latest: GroupDetail | null) => Promise<T>, pick: (result: T) => GroupDetail): Promise<T | null> => {
    pendingSaves += 1;
    set({ isRecalculating: true });
    const run = queue.then(async (): Promise<T | null> => {
      try {
        // `latest` is the previous save's answer in this burst (null for the first), so a queued action can see what the engine
        // did in between: an exclude of a Doubled Build row the engine has already removed becomes a no-op rather than a 404.
        const result = await action(requireGroupId(), lastGoodDetail);
        lastGoodDetail = pick(result);
        return result;
      } catch (error) {
        reportError(error, context);
        return null;
      } finally {
        pendingSaves -= 1;
        if (pendingSaves === 0) {
          if (lastGoodDetail) set(detailSlice(lastGoodDetail));
          lastGoodDetail = null;
          set({ isRecalculating: false });
        }
      }
    });
    queue = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  };

  return {
    loadState: { kind: "idle" },
    group: null,
    totals: null,
    topConditions: [],
    otherVersions: [],
    isRecalculating: false,

    loadGroup: async (groupId, signal) => {
      const alreadyLoaded = get().group?.id === groupId;
      set({ loadState: { kind: "loading" }, ...(alreadyLoaded ? {} : { group: null, totals: null, topConditions: [], otherVersions: [] }) });
      try {
        const detail = await getApi().getGroup(groupId);
        if (signal?.aborted) return;
        set(detailSlice(detail));
        const otherVersions = await loadOtherVersions(detail.group);
        if (!signal?.aborted && get().group?.id === groupId) set({ otherVersions });
      } catch (error) {
        if (signal?.aborted) return;
        const reported = reportError(error, "groupStore.loadGroup");
        set({ loadState: { kind: "error", message: reported.message } });
      }
    },

    applyDetail: (detail) => set(detailSlice(detail)),

    clearGroup: () => set({ loadState: { kind: "idle" }, group: null, totals: null, topConditions: [], otherVersions: [] }),

    updateGroup: async (patch) => (await mutate("groupStore.updateGroup", (groupId) => getApi().updateGroup(groupId, patch), (detail) => detail)) !== null,

    setOverrideMultiplierPreview: (multiplier) =>
      set((state) => {
        if (!state.group || !state.totals) return state;
        return { group: { ...state.group, uwOverrideMultiplier: multiplier }, totals: { ...state.totals, adjustedRmm: adjustedRmm(state.totals.rmm, multiplier) } };
      }),

    // Exclude toggles flip at once and the totals follow the engine's answer: the row is what was clicked, the numbers are
    // visibly recalculating (isRecalculating) until the response replaces the group, and a failed save flips the toggle back.
    toggleMemberExcluded: async (memberId, excluded) => {
      const before = get().group;
      if (before) set({ group: withMemberFields(before, memberId, { isExcluded: excluded }, before.status) });
      const result = await mutate("groupStore.toggleMemberExcluded", (groupId) => getApi().toggleMemberExcluded(groupId, memberId, excluded), (toggled) => toggled.detail);
      if (!result) set((state) => (state.group && before && state.group.id === before.id ? { group: withMemberFields(state.group, memberId, { isExcluded: !excluded }, state.group.status) } : {}));
      if (result && excluded) trackEvent("MemberExcluded", { memberId, autoExcluded: String(result.autoExcludedSpockMemberIds.length) });
      return result;
    },

    // Flag saves are optimistic (Brady, 2026-09-06): the box flips on the click, the save runs behind it, the returned group
    // status lands when it arrives, and a failed save flips the box back alongside the error toast. Only the flag fields get
    // this; anything that can move a debit waits for the engine.
    updateMemberFlags: async (memberId, patch) => {
      const before = get().group;
      const member = before?.members.find((entry) => entry.id === memberId);
      if (!before || !member) return false;
      const previous: MemberFlagsPatch = { irixChecked: member.irixChecked, confirmedValidAuth: member.confirmedValidAuth };
      set({ group: withMemberFields(before, memberId, patch, before.status) });
      try {
        const result = await getApi().setMemberFlags(before.id, memberId, patch);
        set((state) => (state.group?.id === before.id ? { group: { ...state.group, status: result.groupStatus } } : {}));
        return true;
      } catch (error) {
        set((state) => (state.group?.id === before.id ? { group: withMemberFields(state.group, memberId, previous, state.group.status) } : {}));
        reportError(error, "groupStore.updateMemberFlags");
        return false;
      }
    },

    setAssignedRatingIrixSource: async (memberId, assignedRatingId, irixSource) => {
      const before = get().group;
      if (!before) return false;
      set({ group: withRatingFields(before, memberId, assignedRatingId, { irixSource }, before.status) });
      try {
        const result = await getApi().setAssignedRatingFlags(before.id, memberId, assignedRatingId, { irixSource });
        set((state) => (state.group?.id === before.id ? { group: { ...state.group, status: result.groupStatus } } : {}));
        return true;
      } catch (error) {
        set((state) => (state.group?.id === before.id ? { group: withRatingFields(state.group, memberId, assignedRatingId, { irixSource: !irixSource }, state.group.status) } : {}));
        reportError(error, "groupStore.setAssignedRatingIrixSource");
        return false;
      }
    },

    saveMember: async (memberId, draft) => {
      const detail = await mutate(
        "groupStore.saveMember",
        (groupId) => (memberId ? getApi().updateMember(groupId, memberId, { ...draft }) : getApi().addMember(groupId, draft)),
        (saved) => saved,
      );
      if (!detail) return null;
      if (memberId) return detail.group.members.find((member) => member.id === memberId) ?? null;
      return detail.group.members[detail.group.members.length - 1] ?? null;
    },

    removeMember: async (memberId) => (await mutate("groupStore.removeMember", (groupId) => getApi().removeMember(groupId, memberId), (detail) => detail)) !== null,

    addAssignedRatings: async (memberId, request) => {
      const result = await mutate("groupStore.addAssignedRatings", (groupId) => getApi().addAssignedRatings(groupId, memberId, request), (added) => added.detail);
      if (result) {
        trackEvent("RatingAdded", { memberId, count: String(result.created.length), updated: String(result.updated.length), autoAdded: String(result.autoAdded.length) });
        if (request.changeReason) trackEvent("ChangeReasonRecorded", { memberId, changeReason: request.changeReason });
      }
      return result;
    },

    updateAssignedRating: async (memberId, assignedRatingId, patch) => {
      const excludeOnly = patch.isExcluded !== undefined && Object.keys(patch).length === 1;
      const before = get().group;
      if (excludeOnly && before) set({ group: withRatingFields(before, memberId, assignedRatingId, { isExcluded: patch.isExcluded }, before.status) });
      const detail = await mutate(
        "groupStore.updateAssignedRating",
        (groupId, latest) => (latest && !hasRating(latest, memberId, assignedRatingId) ? Promise.resolve(latest) : getApi().updateAssignedRating(groupId, memberId, assignedRatingId, patch)),
        (updated) => updated,
      );
      if (!detail && excludeOnly) set((state) => (state.group && before && state.group.id === before.id ? { group: withRatingFields(state.group, memberId, assignedRatingId, { isExcluded: !patch.isExcluded }, state.group.status) } : {}));
      if (detail && patch.assignedDebits !== undefined) trackEvent("RatingOverridden", { memberId, assignedRatingId });
      if (detail && patch.changeReason) trackEvent("ChangeReasonRecorded", { memberId, changeReason: patch.changeReason });
      return detail !== null;
    },

    removeAssignedRating: async (memberId, assignedRatingId, changeReason = null, changeExplanation = null) => {
      const detail = await mutate(
        "groupStore.removeAssignedRating",
        (groupId, latest) => (latest && !hasRating(latest, memberId, assignedRatingId) ? Promise.resolve(latest) : getApi().removeAssignedRating(groupId, memberId, assignedRatingId, changeReason, changeExplanation)),
        (removed) => removed,
      );
      if (detail && changeReason) trackEvent("ChangeReasonRecorded", { memberId, changeReason });
      return detail !== null;
    },

    sendToSales: async () => {
      const result = await mutate("groupStore.sendToSales", (groupId) => getApi().sendToSales(groupId), (sent) => sent.detail);
      if (result) trackEvent("SendToSales", { groupId: result.detail.group.id, superseded: String(result.supersededGroupIds.length), censusMembers: String(result.census.members.length) });
      return result;
    },

    reopenGroup: async () => {
      const result = await mutate("groupStore.reopenGroup", (groupId) => getApi().reopenGroup(groupId), (reopened) => reopened.detail);
      if (result) trackEvent("GroupCopied", { groupId: result.detail.group.id, reopenedFrom: result.previousVersionGroupId });
      return result;
    },

    restoreVersion: async (mode) => {
      const result = await mutate("groupStore.restoreVersion", (groupId) => getApi().restoreVersion(groupId, { mode }), (restored) => restored.detail);
      if (result) trackEvent("GroupVersionRestored", { groupId: result.detail.group.id, sourceGroupId: result.sourceGroupId, mode });
      return result;
    },
  };
});

export function selectMember(memberId: string | undefined) {
  return (state: GroupState): MemberWithRatings | null => state.group?.members.find((member) => member.id === memberId) ?? null;
}
